import { isEqual } from 'lodash';
import { isUndefined } from 'lodash/fp';

import { ClientDocumentGroup, AgreementTypeDB, CurrentAgreementType, DocumentNameDB, SystemDocumentNameDB, DocumentNameTab, AgreementTypeRelationship, AgreementTypeHierarchy, DocumentNamePageView } from './types';
import { ApplicationState } from '../../../../store/types';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { getDocumentsRoot } from '../../store/selectors';

export const getModalOpen = (state: ApplicationState): boolean => getDocumentsRoot(state).modalOpen;

export const getAllDocumentNames = (state: ApplicationState): DocumentNameDB[] => getDocumentsRoot(state).documents;

export const getAllAdminDocumentNames = (state: ApplicationState): SystemDocumentNameDB[] => getDocumentsRoot(state).adminDocumentNames;

export const getAllAgreementTypes = (state: ApplicationState): AgreementTypeDB[] => getDocumentsRoot(state).savedAgreementTypes;

export const getCurrentAgreementTypes = (state: ApplicationState): CurrentAgreementType[] => getDocumentsRoot(state).currentAgreementTypes;

export const getCurrentDocumentName = (state: ApplicationState): SystemDocumentNameDB | null => getDocumentsRoot(state).currentSystemDocument;

export const getSavedDocumentName = (state: ApplicationState): SystemDocumentNameDB | null => getDocumentsRoot(state).savedSystemDocument;

export const getAvailableDocumentNames = (state: ApplicationState): DocumentNameDB[] => getDocumentsRoot(state).availableDocumentNames;

export const getClientDatasetConfirmationModalOpen = (state: ApplicationState): boolean => getDocumentsRoot(state).clientDatasetConfirmationModalOpen;

export const getCreateDocumentGroupModalOpen = (state: ApplicationState): boolean => getDocumentsRoot(state).createDocumentGroupModalOpen;
export const getLoadingDocumentGroups = (state: ApplicationState): boolean => getDocumentsRoot(state).isLoadingGroups;
export const getAllClientDocumentGroups = (state: ApplicationState): ClientDocumentGroup[] => getDocumentsRoot(state).allDocumentGroups;
export const getSelectedDocumentGroup = (state: ApplicationState): ClientDocumentGroup | null => getDocumentsRoot(state).currentClientDocumentGroup;
export const getIsSavingGroup = (state: ApplicationState): boolean => getDocumentsRoot(state).isSaving;
export const getClientDocumentGroupHasUpdated = (state: ApplicationState) => !isEqual(getDocumentsRoot(state).currentClientDocumentGroup, getDocumentsRoot(state).savedClientDocumentGroup);

export const getAgreementTypeModalOpen = (state: ApplicationState): boolean => getDocumentsRoot(state).agreementTypeModalOpen;

export const getAgreementTypesHaveUpdated = (state: ApplicationState): boolean => getDocumentsRoot(state).agreementTypesHaveUpdated;

export const getAgreementTypesAreUpdating = (state: ApplicationState): boolean => getDocumentsRoot(state).isUpdatingAgreementTypes;

export const getOriginalDocumentNameTableDropdownOptions = (state: ApplicationState): DropdownOption[] => {
    const documentNameOptions = getAvailableDocumentNames(state)
        .filter(({ datasetId }) => datasetId)
        .map(({ documentName }) => ({ value: documentName, label: documentName }));

    const clientGroupOptions = getAllClientDocumentGroups(state);
    // group option values are the clientDocumentNameGroupId, in order to disignuish between dropdown options.
    const mappedGroups = clientGroupOptions.map(({ groupName, clientDocumentGroupId }, i) => ({ value: clientDocumentGroupId!.toString(), label: groupName, showDivider: i === clientGroupOptions.length - 1 }));
    return [...mappedGroups, ...documentNameOptions];
};

export const getMLReadyAgreementTypeIds = (state: ApplicationState): number[] => getDocumentsRoot(state).mlReadyAgreementTypeIds;

export const getIsMLReadyAgreementType = (agreementTypeId: number | undefined) => (state: ApplicationState): boolean => {
    const agreementTypeIds = getMLReadyAgreementTypeIds(state);
    return !isUndefined(agreementTypeId) && agreementTypeIds.includes(agreementTypeId);
};

export const getSelectedDocumentNameTab = (state: ApplicationState): DocumentNameTab => getDocumentsRoot(state).selectedTab;

export const getDocumentNameHasUpdated = (state: ApplicationState): boolean => getDocumentsRoot(state).documentNameHasUpdated;

export const getUnsavedChangeModalOpen = (state: ApplicationState): boolean => getDocumentsRoot(state).unsavedChangesModalOpen;

export const getAgreementTypeRelationships = (state: ApplicationState): AgreementTypeRelationship[] => getDocumentsRoot(state).agreementTypeRelationships;

export const getSelectedAgreementTypeId = (state: ApplicationState): number | null => getDocumentsRoot(state).selectedAgreementTypeId;

export const getCurrentAgreementHierarchy = (state: ApplicationState): AgreementTypeHierarchy => getDocumentsRoot(state).currentAgreementHierarchy;

export const getSavedAgreementHierarchy = (state: ApplicationState): AgreementTypeHierarchy => getDocumentsRoot(state).savedAgreementHierarchy;

export const getCanSaveAgreementHierarchy = (state: ApplicationState): boolean => getDocumentsRoot(state).canSaveAgreementHierarchy;

export const getIsLoadingAgreementHierarchy = (state: ApplicationState): boolean => getDocumentsRoot(state).isLoadingAgreementHierarchy;

export const getDocumentNameView = (state: ApplicationState): DocumentNamePageView => getDocumentsRoot(state).documentNamePageView;

export const getIsGeneratingReport = (state: ApplicationState): boolean => getDocumentsRoot(state).generatingReport;
